import { z } from "zod";

// The vocabulary of a provider setting staff edit in the back office (dev/docs/adrs/
// 008-provider-settings.md): every value defaults to an environment variable, a stored value
// overrides it while set, and clearing it falls back. The owner module (mail, auth) keeps the
// stored values; the back office reads and writes them through the owner's *Api. Shared here so
// every owner answers the same shape and the back office draws one kind of row.

/** Where the value in effect comes from. */
export const settingSourceSchema = z.enum(["stored", "environment"]);
export type SettingSource = z.infer<typeof settingSourceSchema>;

/** One setting as staff see it. A secret's value never leaves the server. */
export const settingViewSchema = z.object({
  /** The value in effect; always null for a secret, and null when nothing sets it. */
  value: z.string().nullable(),
  secret: z.boolean(),
  /** Whether any value is in effect, stored or from the environment. */
  set: z.boolean(),
  /** A secret's last four characters, only when it is long enough that four give nothing away. */
  hint: z.string().nullable(),
  source: settingSourceSchema,
  /** The environment variable that is its default. */
  variable: z.string(),
  /** When and by whom the stored value was written; null while it comes from the environment. */
  updatedAt: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
});
export type SettingView = z.infer<typeof settingViewSchema>;

/** One field's change: a new value to store, or null to clear it back to the environment. A
 * field left out is not touched; a secret is only ever replaced or cleared, never read back. */
export const settingChangeSchema = z.string().trim().min(1).max(2000).nullable();
export type SettingChange = z.infer<typeof settingChangeSchema>;

/** Below this length even four characters say too much about a secret. */
const HINT_MIN_LENGTH = 12;

/** A secret's last four characters, or null when it is short enough that they would give it away. */
export function secretHint(secret: string): string | null {
  return secret.length >= HINT_MIN_LENGTH ? secret.slice(-4) : null;
}

/** A stored value as its owner keeps it: the text (sealed when secret), who wrote it and when. */
export type StoredSetting = {
  readonly value: string;
  readonly updatedAt: Date;
  readonly updatedByUserId: string;
};

/**
 * The value in effect and how staff see it: the stored value while there is one, else the
 * environment's. `open` turns a stored secret back into its text (the owner's secret box).
 */
export function resolveSetting(options: {
  readonly variable: string;
  readonly environment: string | null | undefined;
  readonly stored: StoredSetting | undefined;
  readonly secret?: boolean;
  readonly open?: (sealed: string) => string;
}): { readonly value: string | null; readonly view: SettingView } {
  const secret = options.secret ?? false;
  const stored = options.stored;
  const value = stored
    ? secret && options.open
      ? options.open(stored.value)
      : stored.value
    : (options.environment ?? null) || null;
  return {
    value,
    view: {
      value: secret ? null : value,
      secret,
      set: value !== null,
      hint: secret && value !== null ? secretHint(value) : null,
      source: stored ? "stored" : "environment",
      variable: options.variable,
      updatedAt: stored ? stored.updatedAt.toISOString() : null,
      updatedByUserId: stored ? stored.updatedByUserId : null,
    },
  };
}
